import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { useCached3DObjectIfExists } from "@/object-cache";
import { UnknownObject } from "@/object-cache-type-guard";
import { useAppStore } from "@/store/store-hooks";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { SetCameraView } from "@/utils/cam-view";
import { assert } from "@faro-lotv/foundation";
import {
  IElementGenericImgSheet,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import {
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Camera, OrthographicCamera, Vector3 } from "three";
import {
  ANIMATION_DURATION,
  FlyWalkSceneActiveElement,
} from "../flywalk-types";
import {
  FlyWalkAnimation,
  FlyWalkRenderingAnimation,
  InitialAnimationInfo,
} from "./flywalk-animation";
import { sceneFilter2RenderingState } from "./flywalk-rendering-state";
import {
  pendingEvent2CameraAnimation,
  runToModelAnimation,
} from "./flywalk-to-model-animation";
import { runToOrthomosaicAnimation } from "./flywalk-to-orthomosaic-animation";
import {
  runToPanoAnimation,
  TargetCameraView,
} from "./flywalk-to-pano-animation";
import { runToPhotoAnimation } from "./flywalk-to-photo-animation";
import { useMoveCameraTo } from "./use-move-camera-to";
import { useReactToSceneEvents } from "./use-react-to-scene-events";

export type FlyWalkAnimationData = {
  /** The animation currently active in flywalk mode */
  flyWalkAnimation: FlyWalkAnimation | null;
  /** The scene filter that the rendering pipeline is currently using */
  currentSceneFilter: SceneFilter;
  /** The previously main object */
  previousMain: FlyWalkSceneActiveElement | null;
  /** The renderable object that was previously the main, to be rendered in animations */
  previousMainObject: UnknownObject | null;
  /** Current rotation pivot */
  target: Vector3 | undefined;
  /** Sets the current rotation pivot */
  setTarget(target: Vector3 | undefined): void;
  /** Callback issued when the rendering pipeline finishes the animation */
  endAnimation(): void;
  /** Callback issued to start a simple animation that moves the camera to a new position */
  moveCameraTo(pos: Vector3): void;
  /** Callback issued to start a camera animation to a predefined view */
  setCameraView: SetCameraView;
};

/**
 *
 * @param flyWalkSceneFilter the current scene filter for flywalk mode
 * @param main the current main element for the flywalk scene
 * @param initialAnimationInfo Information about the initial animation when entering flywalk mode
 * @param activeSheetFor2DNavigation The current active sheet to correctly place panorama images
 * @param defaultOrthoCamera The default ortho camera that can be used to render this scene
 * @returns The current animation in flywalk mode if it exists, the previous main object that should
 *  still be rendered during the animation, and a callback to be issued when the animation is  finished.
 */
export function useFlyWalkAnimation(
  flyWalkSceneFilter: SceneFilter,
  main: FlyWalkSceneActiveElement | null,
  initialAnimationInfo: RefObject<InitialAnimationInfo | null>,
  activeSheetFor2DNavigation?: IElementGenericImgSheet,
  defaultOrthoCamera?: OrthographicCamera,
): FlyWalkAnimationData {
  // The animation of the flywalk mode is stored in this state.
  const [flyWalkAnimation, setFlyWalkAnimation] =
    useState<FlyWalkAnimation | null>(null);

  // The previous scene filter is stored in this state. When the scene filter changes,
  // the previous scene filter is used to compute a suitable animation for the transition.
  const [previousSceneFilter, setPreviousSceneFilter] =
    useState<SceneFilter | null>(null);

  // The previous main element is stored in this state. When the main element changes, a transition is performed.
  // During the transition, the previous main element is also rendered.
  const [previousMain, setPreviousMain] =
    useState<FlyWalkSceneActiveElement | null>(null);

  // Loading the previous main object to still render it into an animation
  const previousMainObject = useCached3DObjectIfExists(
    previousMain,
    "FlyWalkPreviousObject",
  );

  // When the animation is finished, the previous scene filter and previous main element are updated
  // to the current ones, and the animation is set to null
  const endAnimation = useCreateEndAnimation(
    setFlyWalkAnimation,
    setPreviousSceneFilter,
    flyWalkSceneFilter,
    setPreviousMain,
    main,
  );

  const camera = useThree((s) => s.camera);

  // moveCameraTo is a callback that, when issued, animates the camera to a given position.
  // It is called for example when the user clicks a point in the minimap.
  const moveCameraTo = useMoveCameraTo(
    camera,
    endAnimation,
    setFlyWalkAnimation,
  );

  const pendingEvent = useRef<TargetCameraView | null>(null);

  const [target, setTarget] = useState<Vector3 | undefined>(
    initialAnimationInfo.current?.target,
  );

  // This hook computes the correct animation for any transition that can happen when a new scene filter
  // or a new main element are selected.
  useComputeAnimationsOnSceneFilterOrMainChange(
    previousSceneFilter,
    flyWalkSceneFilter,
    previousMain,
    main,
    camera,
    pendingEvent,
    initialAnimationInfo,
    endAnimation,
    setFlyWalkAnimation,
    setPreviousSceneFilter,
    activeSheetFor2DNavigation,
    defaultOrthoCamera,
  );

  // This hook computes the animations needed when the user clicks the 'go-to-annotation' button
  const setCameraView = useReactToSceneEvents(
    main,
    camera,
    setFlyWalkAnimation,
    setTarget,
    pendingEvent,
  );

  return useMemo(
    () => ({
      flyWalkAnimation,
      currentSceneFilter: previousSceneFilter ?? flyWalkSceneFilter,
      previousMain,
      previousMainObject,
      target,
      setTarget,
      endAnimation,
      moveCameraTo,
      setCameraView,
    }),
    [
      flyWalkAnimation,
      previousSceneFilter,
      previousMain,
      previousMainObject,
      target,
      setTarget,
      endAnimation,
      flyWalkSceneFilter,
      moveCameraTo,
      setCameraView,
    ],
  );
}

/**
 *
 * @param previousSceneFilter The start scene filter for the animation
 * @param currentSceneFilter The target scene filter of the animation
 * @returns Suitable animation parameters to provide a nice transition
 */
function computeSceneFilterChangedAnimation(
  previousSceneFilter: SceneFilter | null,
  currentSceneFilter: SceneFilter,
): FlyWalkRenderingAnimation {
  return {
    start: sceneFilter2RenderingState(previousSceneFilter),
    end: sceneFilter2RenderingState(currentSceneFilter),
    duration: ANIMATION_DURATION,
  };
}

/**
 * This hook compares the scene filter with the previous value, and the main element with the previous main.
 * If any difference is detected, it computes a suitable animation to e.g. transition from pano to point cloud,
 * or from point cloud to CAD, or from CAD to pano, or from pano1 to pano2, or from photo to pano, or pano to photo
 * etc.
 *
 * @param previousSceneFilter The previous scene filter
 * @param flyWalkSceneFilter The current scene filter
 * @param previousMain The previous main element
 * @param main The current main element
 * @param camera The camera that is rendering the scene
 * @param pendingEvent An event to be executed after a transition
 * @param initialAnimationInfo Information about the initial animation when entering flywalk mode, used to eventually skip it
 * @param endAnimation Callback issued when the animation is finished
 * @param setFlyWalkAnimation Callback to set the computed animation
 * @param setPreviousSceneFilter Callback to set the previous scene filter if it was set to null
 * @param activeSheetFor2DNavigation Sheet to be used to compute the pano positions
 * @param defaultOrthoCamera The default ortho camera that can be used to render this scene
 */
function useComputeAnimationsOnSceneFilterOrMainChange(
  previousSceneFilter: SceneFilter | null,
  flyWalkSceneFilter: SceneFilter,
  previousMain: FlyWalkSceneActiveElement | null,
  main: FlyWalkSceneActiveElement | null,
  camera: Camera,
  pendingEvent: RefObject<TargetCameraView | null>,
  initialAnimationInfo: RefObject<InitialAnimationInfo | null>,
  endAnimation: () => void,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
  setPreviousSceneFilter: (sceneFilter: SceneFilter | null) => void,
  activeSheetFor2DNavigation?: IElementGenericImgSheet,
  defaultOrthoCamera?: OrthographicCamera,
): void {
  const { getState } = useAppStore();

  const { defaultOrthoCamera: mainOrthoCamera } = useActiveCamera();
  const theDefaultOrthoCamera = defaultOrthoCamera ?? mainOrthoCamera;

  useLayoutEffect(() => {
    if (
      noNeedForAnimation(
        previousSceneFilter,
        flyWalkSceneFilter,
        previousMain,
        main,
        setPreviousSceneFilter,
        initialAnimationInfo,
        endAnimation,
      )
    ) {
      return;
    }
    assert(main, "main cannot be null here");

    // Even if the scene filter didn't change, we need to compute this animation
    const renderingAnimation = computeSceneFilterChangedAnimation(
      previousSceneFilter,
      flyWalkSceneFilter,
    );

    if (previousMain === main) {
      setFlyWalkAnimation({
        renderingAnimation,
        cameraAnimation: pendingEvent2CameraAnimation(pendingEvent),
      });
      return;
    }

    const { lookAtElement, animationComputer } =
      initialAnimationInfo.current ?? {};
    initialAnimationInfo.current = null;

    // If the initial animation is already running or has run, early return
    if (
      previousSceneFilter === flyWalkSceneFilter &&
      previousMain === null &&
      animationComputer === undefined &&
      lookAtElement === undefined
    ) {
      return;
    }

    if (isIElementImg360(main)) {
      runToPanoAnimation(
        main,
        previousMain,
        renderingAnimation,
        activeSheetFor2DNavigation,
        camera,
        pendingEvent,
        lookAtElement,
        getState,
        setFlyWalkAnimation,
        theDefaultOrthoCamera,
      );
    } else if (isIElementImg2d(main)) {
      runToPhotoAnimation(
        main,
        previousMain,
        renderingAnimation,
        camera,
        getState,
        setFlyWalkAnimation,
      );
    } else if (isIElementOrthomosaic(main)) {
      runToOrthomosaicAnimation(
        main,
        previousMain,
        renderingAnimation,
        camera,
        getState,
        setFlyWalkAnimation,
      );
    } else {
      runToModelAnimation(
        animationComputer,
        camera,
        renderingAnimation,
        pendingEvent,
        setFlyWalkAnimation,
      );
    }
  }, [
    previousSceneFilter,
    flyWalkSceneFilter,
    previousMain,
    main,
    camera,
    getState,
    activeSheetFor2DNavigation,
    endAnimation,
    setFlyWalkAnimation,
    setPreviousSceneFilter,
    pendingEvent,
    initialAnimationInfo,
    theDefaultOrthoCamera,
  ]);
}

function noNeedForAnimation(
  previousSceneFilter: SceneFilter | null,
  flyWalkSceneFilter: SceneFilter,
  previousMain: FlyWalkSceneActiveElement | null,
  main: FlyWalkSceneActiveElement | null,
  setPreviousSceneFilter: (sceneFilter: SceneFilter) => void,
  initialAnimationInfo: RefObject<InitialAnimationInfo | null>,
  endAnimation: () => void,
): boolean {
  if (previousSceneFilter === null) {
    // The hook is initialized with previousSceneFilter = null and
    // previousMain = null. If we enter here, it means that we are on the very first mount
    // of this hook. We can just set the previous scene filter as the 'flyWalkSceneFilter'
    // and return, because we will enter here another time and run the initial animation
    // to flywalk mode because it will happen that previousMain !== main since previousMain is null.
    setPreviousSceneFilter(flyWalkSceneFilter);
    return true;
  }
  const sceneFilterChanged = previousSceneFilter !== flyWalkSceneFilter;
  const mainChanged = previousMain !== main;

  // If the scene filter didn't change and the main element didn't change, nothing to do.
  if (!sceneFilterChanged && !mainChanged) {
    return true;
  }

  // If we are running the initial animation to flywalk mode, see whether it is possible to skip it.
  if (
    mainChanged &&
    previousMain === null &&
    initialAnimationInfo.current?.canSkipInitialTransition
  ) {
    initialAnimationInfo.current = null;
    endAnimation();
    return true;
  }
  return false;
}

export const INITIAL_FLYWALK_ANIMATION_DATA: FlyWalkAnimationData = {
  flyWalkAnimation: null,
  currentSceneFilter: SceneFilter.Pano,
  previousMain: null,
  previousMainObject: null,
  target: undefined,
  setTarget: () => {},
  endAnimation: () => {},
  moveCameraTo: () => {},
  setCameraView: () => {},
};

/**
 * Computes and returns a callback to be issued at the end of an animation.
 * This is done in a separate function so that the created callback does not
 * retain a larger set of objects which may include the 'previousMainObject'
 * that would not be free to be garbage-collected.
 *
 * @param setFlyWalkAnimation sets the fly walk animation
 * @param setPreviousSceneFilter previous scene filter setter
 * @param flyWalkSceneFilter flywalk scene filter
 * @param setPreviousMain previous main setter
 * @param main main object to dispatch
 * @returns A callback that resets some states and sets the previous main to the main
 */
function useCreateEndAnimation(
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
  setPreviousSceneFilter: (sceneFilter: SceneFilter | null) => void,
  flyWalkSceneFilter: SceneFilter,
  setPreviousMain: (main: FlyWalkSceneActiveElement | null) => void,
  main: FlyWalkSceneActiveElement | null,
): () => void {
  // When the animation is finished, the previous scene filter and previous main element are updated
  // to the current ones, and the animation is set to null
  return useCallback(() => {
    setFlyWalkAnimation(null);
    setPreviousSceneFilter(flyWalkSceneFilter);
    setPreviousMain(main);
  }, [
    flyWalkSceneFilter,
    main,
    setFlyWalkAnimation,
    setPreviousMain,
    setPreviousSceneFilter,
  ]);
}
